import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, FormInstance, Input, InputNumber, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { DEFAULT_CONVERSION_CURRENCY } from 'components/features/submission-details/types';
import SubmissionCurrencyTab from 'components/features/submission-currency-tabs';
import { useWatch } from 'antd/es/form/Form';
import { isNil } from 'lodash';
import { ECurrencies } from 'constants/enums';
import { formatLocaleNumber } from 'utils/format';
import {
  validateBinderPlacementWarnings,
  BinderPlacementFormValues,
} from './form';
import { BasisOfCoverResponseDtoBasisOfCoverEnum } from 'libs/pinnacle-sdk';
import { cn } from 'utils';

interface BinderPlacementSectionProps {
  originalCurrency?: string;
  form: FormInstance;
  contractCurrencyRate: number;
  disabled?: boolean;
}

const BinderPlacementSection: React.FC<BinderPlacementSectionProps> = ({
  form,
  originalCurrency,
  contractCurrencyRate,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(
    originalCurrency || DEFAULT_CONVERSION_CURRENCY
  );
  const [warnings, setWarnings] = useState<Record<string, string>>({});

  const binderShareSigned = useWatch<number>('binderShareSigned', form);
  const limit = useWatch<string>('limit', form);
  const layerPremium = useWatch<string>('layerPremium', form);

  const viewOnlyMode = useMemo(() => {
    if (disabled) return true;
    if (originalCurrency === ECurrencies.USD) return false;

    return activeTab !== originalCurrency;
  }, [activeTab, originalCurrency, disabled]);

  const validateWarnings = useCallback(() => {
    const currentValues = form.getFieldsValue([
      'binderShareSigned',
      'binderShareQuoted',
      'rateMovement',
      'originalRate',
      'adjustedRate',
    ]);
    const formValues: BinderPlacementFormValues = {
      binderShareSigned: currentValues.binderShareSigned,
      binderShareQuoted: currentValues.binderShareQuoted,
      rateMovement: currentValues.rateMovement || 0,
      originalRate: currentValues.originalRate || 0,
      adjustedRate: currentValues.adjustedRate,
    };

    const newWarnings = validateBinderPlacementWarnings(formValues, t);
    setWarnings(newWarnings);
  }, []);

  // Calculate display values for currency conversion without modifying form data
  const displayValues = useMemo(() => {
    const shouldConvert = originalCurrency !== activeTab;
    const rate = shouldConvert ? contractCurrencyRate : 1;
    const values = form.getFieldsValue();
    const {
      totalInsurableValue,
      limit,
      layerPremium,
      deductible,
      excess,
      shareOfLimitSigned,
      shareOfPremiumSigned,
    } = values;

    return {
      totalInsurableValue: !isNil(totalInsurableValue)
        ? Number(Number(totalInsurableValue) * rate).toFixed(2)
        : undefined,
      limit: !isNil(limit)
        ? Number(Number(limit) * rate).toFixed(2)
        : undefined,
      layerPremium: !isNil(layerPremium)
        ? Number(Number(layerPremium) * rate).toFixed(2)
        : undefined,
      deductible: !isNil(deductible)
        ? Number(Number(deductible) * rate).toFixed(2)
        : undefined,
      excess: !isNil(excess)
        ? Number(Number(excess) * rate).toFixed(2)
        : undefined,
      shareOfLimitSigned: !isNil(shareOfLimitSigned)
        ? Math.round(Number(shareOfLimitSigned) * rate).toString()
        : undefined,
      shareOfPremiumSigned: !isNil(shareOfPremiumSigned)
        ? Math.round(Number(shareOfPremiumSigned) * rate).toString()
        : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalCurrency, activeTab, contractCurrencyRate]);

  const isShowingConvertedValues = originalCurrency !== activeTab;

  useEffect(() => {
    if (!isNil(binderShareSigned) && !isNil(limit)) {
      form.setFieldValue(
        'shareOfLimitSigned',
        Math.round((binderShareSigned * Number(limit)) / 100).toString()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binderShareSigned, limit]);

  useEffect(() => {
    if (!isNil(layerPremium) && !isNil(binderShareSigned)) {
      // If isDeclaration = false, use the old calculation logic
      form.setFieldValue(
        'shareOfPremiumSigned',
        Math.round((Number(layerPremium) * binderShareSigned) / 100).toString()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerPremium, binderShareSigned]);

  // TODO: Temporary solution. We need to come up with a pattern to include warning-type validation for form
  const renderWarning = useCallback(
    (field: string) => {
      return (
        warnings[field] && (
          <>
            <div className="text-amber-600">{warnings[field]}</div>
          </>
        )
      );
    },
    [warnings]
  );

  return (
    <FormSection title={t('contractDetail.binderPlacement')} collapsible>
      <SubmissionCurrencyTab
        originalCurrency={originalCurrency}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="custom-tabs-with-bottom-curve-white"
        tabPosition="right"
      />

      <div className="p-4 bg-gray-50 rounded-lg">
        <FormLayoutGroup mdColumns={1}>
          <div
            className={`${!isShowingConvertedValues ? 'block' : 'hidden'} grid grid-cols-1 md:grid-cols-2 gap-4`}
          >
            <Form.Item
              label={t('contractDetail.totalInsurableValue')}
              name="totalInsurableValue"
            >
              <InputNumber
                disabled={viewOnlyMode}
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
                step="0.01"
                precision={2}
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.bookedExchangeRate')}>
              <InputNumber
                disabled
                className="w-full"
                size="large"
                value={contractCurrencyRate}
                formatter={(value) =>
                  formatLocaleNumber(Number(Number(value).toFixed(5)))
                }
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.layerType')} name="layerType">
              <Input disabled size="large" />
            </Form.Item>

            <Form.Item
              label={t('contractDetail.basisOfCover')}
              name="basisOfCover"
            >
              <Select
                disabled={viewOnlyMode}
                size="large"
                placeholder={t('general.selectField', {
                  field: t('contractDetail.basisOfCover'),
                })}
                options={Object.values(
                  BasisOfCoverResponseDtoBasisOfCoverEnum
                ).map((value) => ({
                  label: value,
                  value: value,
                }))}
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.limit')} name="limit">
              <InputNumber
                disabled
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.excess')} name="excess">
              <InputNumber
                disabled
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.deductible')} name="deductible">
              <InputNumber
                disabled
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              label={t('contractDetail.layerPremium')}
              name="layerPremium"
            >
              <InputNumber
                disabled={viewOnlyMode}
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              label={t('contractDetail.binderShareWritten')}
              name="binderShareQuoted"
            >
              <InputNumber
                disabled
                size="large"
                className="w-full"
                addonAfter="%"
                step="0.01"
              />
            </Form.Item>
            <div>
              <Form.Item
                label={t('contractDetail.binderShareSigned')}
                name="binderShareSigned"
                className={cn('', warnings['binderShareSigned'] && 'mb-0')}
              >
                <InputNumber
                  disabled={viewOnlyMode}
                  className="w-full"
                  size="large"
                  addonAfter="%"
                  step="0.0001"
                  precision={4}
                  onChange={validateWarnings}
                />
              </Form.Item>
              {renderWarning('binderShareSigned')}
            </div>
            <Form.Item
              label={t('contractDetail.shareOfLimitSigned')}
              name="shareOfLimitSigned"
            >
              <InputNumber
                disabled
                className="w-full"
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
              />
            </Form.Item>

            <Form.Item
              label={t('contractDetail.shareOfPremiumSigned')}
              name="shareOfPremiumSigned"
            >
              <InputNumber
                disabled
                className="w-full"
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
              />
            </Form.Item>

            <Form.Item
              label={t('contractDetail.rateAdequacy')}
              name="rateAdequacy"
            >
              <InputNumber
                disabled
                className="w-full"
                size="large"
                addonAfter="%"
                step="0.01"
              />
            </Form.Item>

            <Form.Item
              label={t('contractDetail.rateMovement')}
              name="rateMovement"
            >
              <InputNumber
                placeholder={
                  viewOnlyMode ? '' : t('contractDetail.rateMovement')
                }
                className="w-full"
                size="large"
                addonAfter="%"
                step="0.01"
                disabled={viewOnlyMode}
              />
            </Form.Item>
            <Form.Item
              label={t('contractDetail.originalRate')}
              name="originalRate"
            >
              <InputNumber
                placeholder={
                  viewOnlyMode ? '' : t('contractDetail.originalRate')
                }
                className="w-full"
                size="large"
                addonAfter="%"
                step="0.01"
                disabled={viewOnlyMode}
              />
            </Form.Item>
            <Form.Item
              label={t('contractDetail.adjustedRate')}
              name="adjustedRate"
            >
              <InputNumber
                placeholder={
                  viewOnlyMode ? '' : t('contractDetail.adjustedRate')
                }
                className="w-full"
                size="large"
                addonAfter="%"
                step="0.01"
                disabled={viewOnlyMode}
              />
            </Form.Item>
          </div>
          <div
            className={`${isShowingConvertedValues ? 'block' : 'hidden'} grid grid-cols-1 md:grid-cols-2 gap-4`}
          >
            <Form.Item label={t('contractDetail.totalInsurableValue')}>
              <InputNumber
                disabled
                size="large"
                value={displayValues.totalInsurableValue?.toString()}
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.bookedExchangeRate')}>
              <InputNumber
                disabled
                className="w-full"
                size="large"
                value={contractCurrencyRate}
                formatter={(value) => formatLocaleNumber(Number(value))}
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.layerType')}>
              <Input
                disabled
                size="large"
                value={form.getFieldValue('layerType')}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              label={t('contractDetail.basisOfCover')}
              name="basisOfCover"
            >
              <Select
                disabled={viewOnlyMode}
                size="large"
                placeholder={t('general.selectField', {
                  field: t('contractDetail.basisOfCover'),
                })}
                options={Object.values(
                  BasisOfCoverResponseDtoBasisOfCoverEnum
                ).map((value) => ({
                  label: value,
                  value: value,
                }))}
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.limit')}>
              <InputNumber
                disabled
                size="large"
                value={displayValues.limit?.toString()}
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.excess')}>
              <InputNumber
                disabled
                size="large"
                value={displayValues.excess?.toString()}
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.deductible')}>
              <InputNumber
                disabled
                size="large"
                value={displayValues.deductible?.toString()}
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.layerPremium')}>
              <InputNumber
                disabled
                size="large"
                value={displayValues.layerPremium?.toString()}
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.binderShareWritten')}>
              <InputNumber
                disabled
                size="large"
                className="w-full"
                addonAfter="%"
                step="0.01"
                value={form.getFieldValue('binderShareQuoted')}
                precision={2}
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.binderShareSigned')}>
              <InputNumber
                disabled={viewOnlyMode}
                className="w-full"
                size="large"
                addonAfter="%"
                step="0.0001"
                value={form.getFieldValue('binderShareSigned')}
                precision={4}
                onChange={validateWarnings}
              />
            </Form.Item>
            <Form.Item label={t('contractDetail.shareOfLimitSigned')}>
              <InputNumber
                disabled
                className="w-full"
                size="large"
                value={displayValues.shareOfLimitSigned}
                formatter={(value) => formatLocaleNumber(Number(value))}
              />
            </Form.Item>
            {renderWarning('binderShareSigned')}
            <Form.Item label={t('contractDetail.shareOfPremiumSigned')}>
              <InputNumber
                disabled
                className="w-full"
                size="large"
                value={displayValues.shareOfPremiumSigned}
                formatter={(value) => formatLocaleNumber(Number(value))}
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.rateAdequacy')}>
              <InputNumber
                disabled
                size="large"
                value={form.getFieldValue('rateAdequacy')}
                className="w-full"
                addonAfter="%"
              />
            </Form.Item>

            <Form.Item label={t('contractDetail.rateMovement')}>
              <InputNumber
                placeholder={
                  viewOnlyMode ? '' : t('contractDetail.rateMovement')
                }
                className="w-full"
                size="large"
                addonAfter="%"
                step="0.01"
                disabled={viewOnlyMode}
                value={form.getFieldValue('rateMovement')}
              />
            </Form.Item>
          </div>
        </FormLayoutGroup>
      </div>
    </FormSection>
  );
};

export default BinderPlacementSection;
